import React from "react";
import ParticleField from "../ParticleField";
import { Palette } from "../holidayConfig";
import { bareTree, Grain, H, Layer, Mist, mix, ridge, Stars, W } from "./kit";
import { Bat, Cat, Ghost, Pumpkin, Spider, Tombstone } from "./HalloweenCharacters";
import { ToonDefs } from "./toon";
import Enkehus, { ENKEHUS_SPIRE_TOP } from "./Enkehus";
import Zombies, { HILL_TRAIL } from "./Zombies";
import Storm from "./Storm";
import FinalStrike from "./FinalStrike";
import { FINAL_STRIKE_EVENT } from "../events";
import { range } from "./sceneUtils";
import "./scenes.css";

/*
 * Halloween: en stor skördemåne över Borgerskapets änkehus (kontoret) på
 * kullen, zombier från kyrkogården som går till jobbet, fladdermöss
 * som flyger förbi, en kyrkogård med svart katt, glada spöken, lysande
 * lyktgubbar i förgrunden. Ett knotigt träd ramar in scenen
 * och en spindel dinglar i sin tråd.
 */

const SKY_TOP = "#070302";
const SKY_MID = "#170904";
const INK = "#0b0402";
/** Änkehusets port (tornet står under månen, fasaden slutar före skärmkanten). */
const ENKEHUS = { x: 1420, y: 432, s: 0.85 };
/** Toppen av änkehusets tornspira, där finalens blixt slår ner. */
const SPIRE = { x: ENKEHUS.x, y: ENKEHUS.y + ENKEHUS_SPIRE_TOP * ENKEHUS.s };
/** Kullen som änkehuset står på, bakom kyrkogården. */
const HILL_TOP = ENKEHUS.y;

/** Gammal gatlykta av gjutjärn. (x, y) = stolpens fot. */
const GasLamp: React.FC<{ x: number; y: number; p: Palette }> = ({ x, y, p }) => (
  <g transform={`translate(${x} ${y})`}>
    <defs>
      <radialGradient id="hw-lamp-glow">
        <stop offset="0%" stopColor={p[200]} stopOpacity="0.55" />
        <stop offset="35%" stopColor={p[400]} stopOpacity="0.2" />
        <stop offset="100%" stopColor={p[500]} stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="0" cy="-80" r="46" fill="url(#hw-lamp-glow)" className="hol-breathe" />
    <ellipse cx="0" cy="0" rx="26" ry="5" fill={p[400]} opacity="0.18" filter="url(#toon-soft)" />
    <path d="M-5 0 H5 L3 -8 H-3 Z M-1.6 -8 H1.6 L1.1 -70 H-1.1 Z" fill={INK} />
    <path d="M-6 -70 H6 L4 -73 H-4 Z" fill={INK} />
    <path d="M-5 -73 L-6.5 -88 H6.5 L5 -73 Z" fill={p[200]} />
    <path d="M-5 -73 L-6.5 -88 H6.5 L5 -73 Z M0 -73 V-88" fill="none" stroke={INK} strokeWidth="1" />
    <path d="M-8 -88 L0 -96 L8 -88 Z" fill={INK} />
    <circle cx="0" cy="-97.5" r="1.6" fill={INK} />
  </g>
);

const Moon: React.FC<{ p: Palette }> = ({ p }) => (
  <div className="hol-orb" style={{ left: "90.5%", top: "33%", width: "clamp(170px, 19vw, 340px)" }}>
    <svg viewBox="-100 -100 200 200">
      <defs>
        <radialGradient id="hw-halo" cx="50%" cy="50%" r="50%">
          <stop offset="18%" stopColor={p[200]} stopOpacity="0.5" />
          <stop offset="40%" stopColor={p[500]} stopOpacity="0.14" />
          <stop offset="100%" stopColor={p[600]} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="hw-moon" cx="42%" cy="38%" r="68%">
          <stop offset="0%" stopColor={p[50]} />
          <stop offset="55%" stopColor={p[100]} />
          <stop offset="100%" stopColor={p[300]} />
        </radialGradient>
      </defs>
      <circle r="100" fill="url(#hw-halo)" />
      <circle r="50" fill="url(#hw-moon)" />
      <g fill={p[300]} opacity="0.45">
        <circle cx="-16" cy="-13" r="10" />
        <circle cx="18" cy="10" r="8" />
        <circle cx="-8" cy="24" r="6" />
        <circle cx="21" cy="-21" r="4.5" />
        <circle cx="-29" cy="10" r="4" />
      </g>
    </svg>
  </div>
);

/** Molnslöjor som glider förbi månen. */
const Clouds: React.FC = () => (
  <div className="hol-clouds">
    <svg viewBox="0 0 1600 300" preserveAspectRatio="xMidYMid slice">
      <g fill={INK} opacity="0.55" filter="url(#toon-softer)">
        <ellipse cx="1100" cy="170" rx="180" ry="12" />
        <ellipse cx="1260" cy="190" rx="140" ry="9" />
        <ellipse cx="980" cy="210" rx="120" ry="8" />
        <ellipse cx="300" cy="120" rx="160" ry="10" />
      </g>
    </svg>
  </div>
);

const Fence: React.FC<{ from: number; to: number; y: (x: number) => number; fill: string }> = ({ from, to, y, fill }) => {
  const posts = range(Math.floor((to - from) / 14) + 1).map(i => from + i * 14);
  return (
    <g fill={fill} stroke={fill}>
      <path d={`M${from} ${y(from) - 30} ${posts.map(px => `L${px} ${y(px) - 30}`).join(" ")}`} strokeWidth="2.5" fill="none" />
      <path d={`M${from} ${y(from) - 12} ${posts.map(px => `L${px} ${y(px) - 12}`).join(" ")}`} strokeWidth="2.5" fill="none" />
      {posts.map(px => (
        <path key={px} d={`M${px - 1.6} ${y(px) + 4} V${y(px) - 38} L${px} ${y(px) - 44} L${px + 1.6} ${y(px) - 38} V${y(px) + 4} Z`} stroke="none" />
      ))}
    </g>
  );
};

interface Flyer {
  top: string;
  width: string;
  duration: number;
  delay: number;
  speed: number;
  reverse?: boolean;
}

const BATS: Flyer[] = [
  { top: "16%", width: "clamp(70px, 7vw, 120px)", duration: 24, delay: -4, speed: 0.34 },
  { top: "24%", width: "clamp(52px, 5vw, 86px)", duration: 28, delay: -10, speed: 0.3 },
  { top: "11%", width: "clamp(40px, 3.8vw, 64px)", duration: 32, delay: -19, speed: 0.28 },
  { top: "32%", width: "clamp(60px, 5.6vw, 96px)", duration: 36, delay: -26, speed: 0.36, reverse: true },
  { top: "22%", width: "clamp(38px, 3.4vw, 58px)", duration: 30, delay: -14, speed: 0.26, reverse: true },
];

const HalloweenScene: React.FC<{ p: Palette; dark: boolean }> = ({ p, dark }) => {
  const rFar = ridge({ seed: 41, base: 452, amp: 120, scale: 2.2, shape: x => 60 * Math.exp(-Math.pow((x - 1180) / 220, 2)) });
  const rMid = ridge({ seed: 57, base: 515, amp: 50, scale: 2, shape: x => 30 * Math.exp(-Math.pow((x - 330) / 280, 2)) });
  const rNear = ridge({
    seed: 63,
    base: 585,
    amp: 26,
    scale: 2,
    // Till höger sjunker förgrunden så att änkehusets tomt syns.
    shape: x => (x < 800 ? 70 * Math.pow((800 - x) / 800, 2.2) : -12 * Math.min(1, (x - 800) / 350)),
  });
  // Finalen: memen i hjulet säger till när blixten ska slå ner i tornet.
  // Varje nedslag räknas upp så att det spelas upp på nytt nästa gång.
  const [strikes, setStrikes] = React.useState(0);
  React.useEffect(() => {
    const onFinal = () => setStrikes(n => n + 1);
    window.addEventListener(FINAL_STRIKE_EVENT, onFinal);
    return () => window.removeEventListener(FINAL_STRIKE_EVENT, onFinal);
  }, []);
  const farFill = mix(p[900], SKY_MID, 0.6);
  const midFill = mix(p[950], INK, 0.45);
  const hillFill = mix(farFill, midFill, 0.55);

  return (
    <>
      <ToonDefs />
      <div
        className="hol-sky-fill"
        style={{
          background: `radial-gradient(circle at 90% 33%, ${mix(p[600], SKY_MID, 0.5)} 0%, transparent 34%), radial-gradient(ellipse 90% 50% at 50% 100%, ${mix(p[600], SKY_MID, 0.35)} 0%, transparent 70%), linear-gradient(180deg, ${SKY_TOP} 0%, ${SKY_MID} 40%, ${mix(p[950], SKY_MID, 0.3)} 70%, ${mix(p[800], SKY_MID, 0.5)} 100%)`,
        }}
      />
      <Stars seed={31} count={140} color={p[100]} maxY={420} />
      <Moon p={p} />
      <Storm p={p} />
      <Clouds />
      {BATS.map((b, i) => (
        <div
          key={i}
          className={`hol-flight ${b.reverse ? "hol-flight-reverse" : ""} ${i > 2 ? "hol-hide-sm" : ""}`}
          style={{ top: b.top, width: b.width, animationDuration: `${b.duration}s`, animationDelay: `${b.delay}s` }}
        >
          <div className="hol-flight-inner" style={{ animationDuration: `${1.6 + i * 0.3}s` }}>
            <Bat p={p} speed={b.speed} />
          </div>
        </div>
      ))}

      <Layer delay={0}>
        <path d={rFar.d} fill={farFill} />
        <path d={bareTree(960, rFar.y(960) + 4, 26, 5, 8, 5)} fill={mix(farFill, INK, 0.3)} />
      </Layer>
      <Mist bottom="22vh" height="16vh" color={`${p[500]}26`} duration={60} />
      {/* Änkehuset på kullen bakom kyrkogården, med stigen upp från gravarna */}
      <Layer delay={70}>
        <path
          d={`M1030 ${H} C 1110 ${HILL_TOP + 76}, 1180 ${HILL_TOP + 4}, 1262 ${HILL_TOP - 2} C 1330 ${HILL_TOP - 7}, 1560 ${HILL_TOP - 8}, ${W + 40} ${HILL_TOP - 4} V${H} Z`}
          fill={hillFill}
        />
        <path d={`M1030 ${H} C 1110 ${HILL_TOP + 76}, 1180 ${HILL_TOP + 4}, 1262 ${HILL_TOP - 2}`} stroke={mix(hillFill, p[500], 0.25)} strokeWidth="2" fill="none" opacity="0.6" />
        <g transform={`translate(${ENKEHUS.x} ${ENKEHUS.y}) scale(${ENKEHUS.s})`}>
          <path
            d={`M${HILL_TRAIL[0].join(" ")} C -270 80, -230 58, ${HILL_TRAIL[1].join(" ")} S -130 30, ${HILL_TRAIL[2].join(" ")} S -40 19, 0 19`}
            stroke={mix(hillFill, "#a07a5c", 0.3)}
            strokeWidth="12"
            strokeLinecap="round"
            fill="none"
          />
          <GasLamp x={-172} y={14} p={p} />
        </g>
        <Enkehus x={ENKEHUS.x} y={ENKEHUS.y} s={ENKEHUS.s} p={p} />
        <Zombies x={ENKEHUS.x} y={ENKEHUS.y} s={ENKEHUS.s} />
      </Layer>
      <Layer delay={140}>
        <path d={rMid.d} fill={midFill} />
        <Fence from={80} to={600} y={x => rMid.y(x) + 6} fill={mix(midFill, "#000000", 0.4)} />
        <path d={bareTree(660, rMid.y(660) + 6, 48, 8, 12, 6)} fill={mix(midFill, "#000000", 0.4)} />
        <path d={bareTree(1040, rMid.y(1040) + 6, 38, 7, 19, 6)} fill={mix(midFill, "#000000", 0.4)} />
        <Tombstone x={150} y={rMid.y(150) + 10} s={0.7} tilt={-4} p={p} />
        <Tombstone x={250} y={rMid.y(250) + 10} s={0.85} tilt={3} p={p} kind="cross" />
        <Tombstone x={350} y={rMid.y(350) + 10} s={0.9} tilt={-2} p={p} />
        <Tombstone x={470} y={rMid.y(470) + 10} s={0.75} tilt={5} p={p} kind="cross" />
        <Tombstone x={560} y={rMid.y(560) + 10} s={0.65} tilt={-6} p={p} />
        <Cat x={350} y={rMid.y(350) + 10 - 62} s={0.62} p={p} />
        <Tombstone x={1120} y={rMid.y(1120) + 10} s={0.7} tilt={4} p={p} />
        {/* Gravarna som zombierna kliver upp ur, vid foten av stigen */}
        <Tombstone x={1178} y={rMid.y(1178) + 10} s={0.55} tilt={-7} p={p} kind="cross" />
        <Tombstone x={1236} y={rMid.y(1236) + 10} s={0.5} tilt={6} p={p} />
      </Layer>
      <Mist bottom="4vh" height="16vh" color={`${p[300]}1c`} duration={45} reverse />
      <Layer delay={280}>
        <path d={rNear.d} fill={INK} />
        {/* Det stora knotiga trädet till vänster */}
        <path d={bareTree(70, rNear.y(70) + 10, 170, 40, 3, 8, -Math.PI / 2 + 0.12)} fill={INK} />
        {/* Gräset längs gatan framför änkehuset */}
        <path
          d={range(34)
            .map(i => {
              const gx = 1150 + i * 14;
              const gy = rNear.y(gx) + 2;
              return `M${gx} ${gy} l3 ${-9 - (i % 3) * 3} l2 ${9 + (i % 3) * 3} l4 ${-6 - (i % 2) * 4} l2 ${6 + (i % 2) * 4} Z`;
            })
            .join(" ")}
          fill={INK}
        />
        <Pumpkin x={1478} y={rNear.y(1478) + 26} s={0.85} p={p} face="happy" delay={-1.2} />
        <Pumpkin x={1532} y={rNear.y(1532) + 30} s={0.6} p={p} face="none" />
        <Pumpkin x={1580} y={rNear.y(1580) + 28} s={1.05} p={p} face="spooky" delay={-2} />
        <Pumpkin x={230} y={rNear.y(230) + 26} s={1.1} p={p} face="happy" delay={-0.6} />
        <Pumpkin x={320} y={rNear.y(320) + 26} s={0.72} p={p} face="none" />
        <Pumpkin x={140} y={rNear.y(140) + 28} s={0.85} p={p} face="spooky" delay={-1.7} />
      </Layer>

      {/* Spöken som svävar */}
      <div className="hol-float-el hol-hide-sm" style={{ left: "4%", top: "40%", width: "clamp(80px, 8.5vw, 150px)" }}>
        <div className="hol-hover">
          <Ghost p={p} />
        </div>
      </div>
      {/* Det förskräckta spöket flyr när finalens blixt slår ner */}
      <div
        className={`hol-float-el hol-hide-sm ${strikes > 0 ? "hol-ghost-flee" : ""}`}
        style={{ right: "4.5%", top: "44%", width: "clamp(64px, 6.5vw, 116px)" }}
      >
        <div className="hol-hover" style={{ animationDuration: "9s", animationDelay: "-3s" }}>
          <Ghost p={p} mood="surprised" />
        </div>
      </div>
      {/* Spindeln dinglar i sin tråd */}
      <div className="hol-spider hol-hide-md" style={{ left: "81.5%" }}>
        <div className="hol-spider-thread" />
        <div className="hol-spider-body">
          <Spider p={p} />
        </div>
      </div>

      {/* Finalen ovanpå spöken och spindel: himlen lyser upp, blixten slår ner
          i tornspiran och texten blir kvar. Eget lager i samma koordinater som
          landskapet, men utan entrén så att det står still när det dyker upp. */}
      {strikes > 0 && (
        <>
          <div
            key={strikes}
            className="hol-final-flash"
            style={{ background: `radial-gradient(ellipse 55% 75% at 88% 55%, rgba(255,248,235,0.7), ${p[300]}30 45%, transparent 75%)` }}
          />
          <div className="hol-layer hol-layer-static">
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMax slice" xmlns="http://www.w3.org/2000/svg">
              <FinalStrike x={SPIRE.x} y={SPIRE.y} p={p} strike={strikes} />
            </svg>
          </div>
        </>
      )}

      <ParticleField kind="leaves" dark={dark} density={0.6} />
      <ParticleField kind="embers" dark={dark} density={0.7} />
      <Grain opacity={0.07} />
    </>
  );
};

export default HalloweenScene;
